import { useEffect, useRef, useState } from 'react';

const lessonSteps = [
  {
    time: '前 5 分鐘',
    icon: 'ri-chat-smile-3-line',
    title: '先聊聊這週的狀態',
    desc: '不急著拿起琴，先問問你這週過得怎麼樣。工作很忙？狀態不太好？還是今天特別有精神？這些都會影響這堂課的節奏，也是我最在意的事。',
    color: '#9a8470',
    bg: '#f5f0e8',
  },
  {
    time: '10–15 分鐘',
    icon: 'ri-loop-left-line',
    title: '回顧上次學的內容',
    desc: '不是測驗，不是驗收作業。而是一起感受一下上次學的東西，有沒有在你心裡留下什麼——彈起來哪裡順、哪裡卡，這些都是很好的對話起點。',
    color: '#7a8b6e',
    bg: '#eaede5',
  },
  {
    time: '30–35 分鐘',
    icon: 'ri-music-2-line',
    title: '今天的學習主軸',
    desc: '可能是繼續上次的內容，可能是新的技法，也可能是你說「我想彈這首曲子」而展開的探索。我們不趕，讓每個動作在你的身體和耳朵裡慢慢落地。',
    color: '#7a6248',
    bg: '#f5f0e8',
  },
  {
    time: '最後 10 分鐘',
    icon: 'ri-plant-line',
    title: '整理感受，帶走一個方向',
    desc: '課快結束前，我們會一起整理今天的感受：有什麼讓你覺得「啊，原來是這樣」的時刻？下次回家可以怎麼陪陪它？不是一份作業清單，而是一個讓你想繼續的方向。',
    color: '#4a6741',
    bg: '#eaede5',
  },
];

const noWorryItems = [
  { icon: 'ri-close-circle-line', text: '不需要會看譜——我們從零開始，一步一步來' },
  { icon: 'ri-close-circle-line', text: '不需要買琴——教室提供上課用琴，確認喜歡再考慮' },
  { icon: 'ri-close-circle-line', text: '不需要有音樂基礎——絕大多數學生都是零基礎開始' },
  { icon: 'ri-close-circle-line', text: '不需要背負進度壓力——這裡沒有進度表，只有你的節奏' },
  { icon: 'ri-close-circle-line', text: '不需要擔心年齡——成人初學者的比例遠高於兒童' },
];

const locationInfo = {
  icon: 'ri-building-2-line',
  label: '實體課',
  location: '台中豐原教室',
  duration: '每堂 60 分鐘',
  detail: '面對面授課，可以即時調整手型與觸弦方式，是最推薦的上課形式。教室環境安靜舒適，每堂課都是一段不被干擾的專注時光。',
  address: '豐原大道一段269-1號',
};

function useVisible(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function LessonFlowSection() {
  const { ref, visible } = useVisible();

  return (
    <section className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px bg-[#b08060]"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">
                  A Typical Lesson
                </span>
              </div>
              <h2
                className="font-serif-tc text-[#2c2420] leading-[1.3]"
                style={{ fontSize: 'clamp(1.625rem, 3vw, 2.25rem)' }}
              >
                一堂課的樣子
              </h2>
            </div>
            <p className="text-[0.9375rem] leading-[1.75] text-[#7a6e62] max-w-sm">
              每堂課不是固定的腳本，而是一個會根據你的狀態調整的對話。但大致上，它會是這樣的流動。
            </p>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {lessonSteps.map((step, i) => (
              <div
                key={i}
                className="relative rounded-2xl border border-[#ede5d8] overflow-hidden flex flex-col transition-all duration-700 group hover:-translate-y-1"
                style={{ background: step.bg, transitionDelay: `${i * 0.08}s` }}
              >
                {/* Step number */}
                <div className="px-6 pt-6 pb-0 flex items-center justify-between">
                  <span
                    className="text-[2.75rem] font-bold leading-none select-none"
                    style={{ color: step.color, opacity: 0.12 }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div
                    className="w-10 h-10 flex items-center justify-center rounded-xl shadow-sm"
                    style={{ background: 'white' }}
                  >
                    <i className={`${step.icon} text-lg`} style={{ color: step.color }}></i>
                  </div>
                </div>

                <div className="px-6 pb-7 pt-3 flex flex-col flex-1">
                  {/* Time badge */}
                  <span
                    className="inline-block px-2.5 py-0.5 rounded-full text-[0.72rem] font-semibold mb-3 w-fit"
                    style={{ background: 'white', color: step.color }}
                  >
                    {step.time}
                  </span>
                  <h3
                    className="font-serif-tc text-[#2c2420] leading-[1.4] mb-3 font-bold"
                    style={{ fontSize: '1.0625rem' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-[0.875rem] leading-[1.75] text-[#5a4e42] flex-1">
                    {step.desc}
                  </p>
                </div>

                {/* Arrow connector (desktop) */}
                {i < lessonSteps.length - 1 && (
                  <div className="hidden lg:flex absolute -right-2.5 top-1/2 -translate-y-1/2 z-10 w-6 h-6 items-center justify-center bg-[#faf8f5] rounded-full border border-[#ede5d8]">
                    <i className="ri-arrow-right-s-line text-[#c8b89a] text-sm"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Progress indicator dots */}
          <div className="flex items-center justify-center gap-2 mb-14 lg:hidden">
            {lessonSteps.map((step, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full"
                style={{ background: step.color, opacity: 0.35 }}
              ></div>
            ))}
          </div>

          {/* 你不需要擔心的是 */}
          <div className="bg-[#f2ede5] rounded-2xl border border-[#ede5d8] p-8 lg:p-10 mb-10">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white shrink-0">
                <i className="ri-emotion-happy-line text-[#b08060] text-lg"></i>
              </div>
              <h3 className="font-serif-tc text-[#2c2420] font-bold" style={{ fontSize: '1.1875rem' }}>
                你不需要擔心的是
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {noWorryItems.map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/60 rounded-xl p-4">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className={`${item.icon} text-[#b08060] text-base`}></i>
                  </div>
                  <p className="text-[0.9375rem] leading-[1.7] text-[#4a3e32]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 實體課資訊 */}
          <div className="bg-white rounded-2xl border border-[#ede5d8] p-8 lg:p-10 flex flex-col lg:flex-row gap-8 items-start">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#f5f0e8] shrink-0">
              <i className={`${locationInfo.icon} text-[#7a6248] text-2xl`}></i>
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h4 className="font-serif-tc text-[#2c2420] font-bold" style={{ fontSize: '1.25rem' }}>
                  {locationInfo.label}
                </h4>
                <span className="text-[0.8125rem] text-[#7a6248] bg-[#f5f0e8] px-3 py-1 rounded-full font-medium whitespace-nowrap">
                  {locationInfo.duration}
                </span>
                <span className="text-[0.8125rem] text-[#9a8470] bg-[#ede8e0] px-3 py-1 rounded-full font-medium">
                  {locationInfo.location}
                </span>
              </div>
              <p className="text-[1rem] leading-[1.75] text-[#5a4e42] mb-4">{locationInfo.detail}</p>
              <div className="flex items-center gap-2 text-[0.875rem] text-[#7a6e62]">
                <i className="ri-map-pin-line text-[#b08060]"></i>
                <span>{locationInfo.address}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}